(() => {
  'use strict';

  const VERSION = '1.12.1';
  const ANIMATION_VERSION = '1.11.0';
  const EFFECTS_VERSION = '1.11.0';
  const FADE_DURATION = 1200;
  const HOLD_DURATION = 10000;
  const TRANSITION_AT = FADE_DURATION + HOLD_DURATION;
  const DURATION = TRANSITION_AT + FADE_DURATION;
  const FLOCK_DURATION = 19000;
  const SPIDERS_DURATION = 48000;
  const SPIDERS_VERSION = '1.12.1';
  const RETRY_DELAY = 20000;
  const TAG = 'auros-halloween';
  const MODULES = window.AurosHalloweenModules = window.AurosHalloweenModules || {};
  const resources = MODULES.resources = MODULES.resources || new Map();
  const documentRoot = document.documentElement;
  const editorSelector = '[id^="pbx-editor-modal_"],iframe[src*="getprintbox"],iframe[src*="printbox"]';
  const editorAttributes = ['open', 'hidden', 'aria-hidden', 'class', 'style'];
  const removedCharacters = new Set(['bats', 'black_cat', 'eyeball', 'grimoire', 'cauldron', 'spider']);
  const clamp = (value, min, max, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
  };
  const boolean = (value, fallback = true) => typeof value === 'boolean' ? value : fallback;
  const color = (value, fallback) => /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i.test(String(value || '')) ? value : fallback;
  const now = () => performance.now();
  const random = (min = 0, max = 1) => min + Math.random() * (max - min);

  function normalize(raw) {
    return {
      enabled: raw.enabled === true,
      scope: 'all',
      initialDelay: 0,
      interval: TRANSITION_AT / 1000,
      duration: DURATION / 1000,
      fadeDuration: FADE_DURATION / 1000,
      holdDuration: HOLD_DURATION / 1000,
      flockDuration: FLOCK_DURATION / 1000,
      mobile: boolean(raw.mobile),
      flock: boolean(raw.flock, boolean(raw.ambientBats, raw.characters?.bats !== false)),
      intensity: raw.intensity === 'standard' ? 'standard' : 'subtle',
      scale: clamp(raw.scale, 60, 120, 90) / 100,
      characterOpacity: clamp(raw.characterOpacity, .88, .98, .94),
      accent: color(raw.accent, '#00c8ff'),
      secondary: color(raw.secondary, '#8b5cf6'),
      warmAccent: color(raw.warmAccent, '#ff7a00'),
      glow: boolean(raw.glow), mist: boolean(raw.mist), particles: boolean(raw.particles), scenery: boolean(raw.scenery),
      cast: raw.cast === 'custom' ? 'custom' : 'all',
      characters: raw.characters && typeof raw.characters === 'object' ? raw.characters : {},
      pageType: String(raw.pageType || ''),
      templateSuffix: String(raw.templateSuffix || ''),
      designMode: raw.designMode === true,
      assets: raw.assets && typeof raw.assets === 'object' ? raw.assets : {}
    };
  }

  function visible(element) {
    if (!element || !element.isConnected || element.hidden || !element.getClientRects().length) return false;
    for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    }
    return true;
  }

  function loadResource(url, kind) {
    if (typeof url !== 'string' || !url) return Promise.reject(new Error('Missing Halloween asset: ' + kind));
    let address;
    try {
      address = new URL(url, document.baseURI);
      if (!['https:', 'http:'].includes(address.protocol)) throw new Error('Unsupported asset protocol');
    } catch (error) { return Promise.reject(error); }
    const key = kind + ':' + address.href;
    if (resources.has(key)) return resources.get(key);
    const promise = new Promise((resolve, reject) => {
      const node = document.createElement(kind === 'css' ? 'link' : 'script');
      node.dataset.aurosHalloweenResource = kind;
      let timer;
      const finish = error => {
        clearTimeout(timer);
        node.onload = null;
        node.onerror = null;
        if (error) { node.remove(); reject(error); }
        else { node.dataset.loaded = 'true'; resolve(node); }
      };
      node.onload = () => finish();
      node.onerror = () => finish(new Error('Unable to load Halloween ' + kind));
      if (kind === 'css') { node.rel = 'stylesheet'; node.href = address.href; }
      else { node.async = true; node.src = address.href; }
      timer = setTimeout(() => finish(new Error('Halloween asset timeout: ' + kind)), 15000);
      document.head.appendChild(node);
    });
    resources.set(key, promise);
    promise.catch(() => resources.delete(key));
    return promise;
  }

  class AurosHalloween extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this._mounted) this._mount(); });
    }

    disconnectedCallback() { this._unmount(); }

    _mount() {
      this.hidden = true;
      const source = this.querySelector('script[data-ah-config]');
      this.stage = this.querySelector('[data-ah-stage]');
      this.flockStage = this.querySelector('[data-ah-flock-stage]');
      if (!source || !this.stage) return;
      try { this.config = normalize(JSON.parse(source.textContent)); }
      catch (_) { this.dataset.state = 'invalid-config'; return; }
      this._mounted = true;
      this._disposed = false;
      this._manualPause = false;
      this._pageAway = false;
      this._pageReady = document.readyState === 'complete';
      this._loadToken = (this._loadToken || 0) + 1;
      this._state = 'idle';
      this._error = '';
      this._retryAt = 0;
      this._timer = 0;
      this._timerAt = 0;
      this._editorTimer = 0;
      this._editorTransitionUntil = 0;
      this._checking = false;
      this._pendingEditorScan = false;
      this._editorNodes = new Set();
      this._editorAncestors = new Set();
      this._editorAttributeState = new Map();
      this._active = null;
      this._scenes = new Map();
      this._loading = null;
      this._resourcesReady = false;
      this._nextAt = 0;
      this._shown = 0;
      this._lastCharacter = '';
      this._lastSide = '';
      this._nextSide = 'left';
      this._queue = [];
      this._cycle = 0;
      this._figureMetrics = new Map();
      this._layoutWidth = documentRoot.clientWidth || innerWidth;
      this._abort = new AbortController();
      this._reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this._mobile = matchMedia('(max-width: 749px)');
      this._catalog = Array.from(this.querySelectorAll('template[data-ah-character]')).filter(template => {
        if (removedCharacters.has(template.dataset.ahCharacter)) return false;
        const key = template.dataset.ahSetting || 'extra_characters';
        return this.config.cast === 'all' || this.config.characters[key] === true;
      });
      this._ids = new Set(this._catalog.map(template => template.dataset.ahCharacter));
      if (this._flockAllowed()) this._ids.add('bats');
      if (this.config.cast === 'all' || this.config.characters.spider === true) this._ids.add('spiders');
      this.dataset.glow = String(this.config.glow);
      this.dataset.intensity = this.config.intensity;
      this.style.setProperty('--ah-accent', this.config.accent);
      this.style.setProperty('--ah-secondary', this.config.secondary);
      this.style.setProperty('--ah-warm', this.config.warmAccent);
      this.style.setProperty('--ah-scale', String(this.config.scale));
      this.style.setProperty('--ah-character-opacity', String(this.config.characterOpacity));

      const signal = this._abort.signal;
      const listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal });
      const reconcile = () => this._reconcile();
      listen(window, 'load', () => { this._pageReady = true; this._reconcile(); }, { once: true });
      listen(document, 'visibilitychange', reconcile);
      listen(window, 'pagehide', () => { this._pageAway = true; this._reconcile(); });
      listen(window, 'pageshow', event => {
        this._pageAway = false;
        this._pageReady = document.readyState === 'complete';
        if (event.persisted) {
          this._finishScene(true);
          this._shown = 0;
          this._lastCharacter = '';
          this._lastSide = '';
          this._nextSide = 'left';
          this._queue = [];
          this._cycle = 0;
          this._nextAt = 0;
        }
        this._reconcile();
      });
      listen(window, 'popstate', reconcile);
      listen(window, 'hashchange', reconcile);
      const resize = () => {
        const width = documentRoot.clientWidth || innerWidth;
        if (width === this._layoutWidth) { this._reconcile(); return; }
        this._layoutWidth = width;
        if (this._active) {
          this._interruptScenes();
        }
        this._reconcile();
      };
      listen(window, 'resize', resize, { passive: true });
      if (window.visualViewport) listen(window.visualViewport, 'resize', resize, { passive: true });
      listen(this._reduced, 'change', reconcile);
      listen(this._mobile, 'change', reconcile);
      listen(window, 'auros:printbox-editor-triggered', () => {
        this._editorTransitionUntil = now() + 15000;
        clearTimeout(this._editorTimer);
        this._editorTimer = setTimeout(() => {
          this._editorTimer = 0;
          this._editorTransitionUntil = 0;
          this._reconcile();
        }, 15000);
        this._reconcile();
      });
      this._editorObserver = new MutationObserver(records => {
        let changed = false;
        for (const record of records) {
          const state = this._editorAttributeState.get(record.target);
          if (!state) continue;
          const value = this._editorAttributeValue(record.target, record.attributeName);
          if (state[record.attributeName] === value) continue;
          state[record.attributeName] = value;
          changed = true;
        }
        if (changed) this._queueEditorCheck(false);
      });
      this._observer = new MutationObserver(records => {
        for (const record of records) {
          if (this.contains(record.target)) continue;
          if (record.type === 'attributes') {
            if (this._editorNodes.has(record.target) || record.target.matches(editorSelector)) {
              this._queueEditorCheck(true);
              return;
            }
            continue;
          }
          for (const node of record.removedNodes) {
            if (this._editorNodes.has(node) || this._editorAncestors.has(node)) {
              this._queueEditorCheck(true);
              return;
            }
          }
          for (const node of record.addedNodes) {
            if (node.nodeType !== 1 || this.contains(node)) continue;
            if (node.matches(editorSelector) || node.querySelector(editorSelector)) {
              this._queueEditorCheck(true);
              return;
            }
          }
        }
      });
      this._observer.observe(documentRoot, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['src', 'id']
      });
      this._watchEditors();
      MODULES.instance = this;
      this._reconcile();
    }

    _editorAttributeValue(element, attribute) {
      if (attribute === 'style') return ['display', 'visibility', 'opacity', 'content-visibility'].map(property => element.style.getPropertyValue(property)).join('|');
      return element.getAttribute(attribute);
    }

    _watchEditors() {
      this._editorObserver.disconnect();
      this._editorNodes = new Set(document.querySelectorAll(editorSelector));
      this._editorAncestors.clear();
      const watched = new Set([documentRoot, document.body]);
      for (const editor of this._editorNodes) {
        for (let node = editor; node && node.nodeType === 1; node = node.parentElement) {
          watched.add(node);
          if (node !== editor) this._editorAncestors.add(node);
        }
      }
      this._editorAttributeState.clear();
      for (const node of watched) {
        if (!node) continue;
        const state = Object.create(null);
        for (const attribute of editorAttributes) state[attribute] = this._editorAttributeValue(node, attribute);
        this._editorAttributeState.set(node, state);
        this._editorObserver.observe(node, { attributes: true, attributeFilter: editorAttributes });
      }
    }

    _queueEditorCheck(rescan) {
      this._pendingEditorScan = this._pendingEditorScan || rescan;
      if (this._checking) return;
      this._checking = true;
      queueMicrotask(() => {
        this._checking = false;
        if (!this._mounted || this._disposed) return;
        if (this._pendingEditorScan) {
          this._pendingEditorScan = false;
          this._watchEditors();
        }
        this._reconcile();
      });
    }

    _editorActive() {
      const editorVisible = document.body.classList.contains('pbx-editor-body-hidden') ||
        Array.from(this._editorNodes).some(visible);
      if (editorVisible) {
        this._editorTransitionUntil = 0;
        clearTimeout(this._editorTimer);
        this._editorTimer = 0;
        return true;
      }
      const path = location.pathname.replace(/\/+$/, '');
      const params = new URLSearchParams(location.search);
      return this.config.templateSuffix === 'only_config' || params.get('view') === 'only_config' ||
        /\/(?:products|pages)\/[^/]+\/editor$/.test(path) ||
        (params.has('auros_kit_session') && params.has('auros_kit_action')) ||
        (/\/cart$/.test(path) && params.get('auros_kit_reopen') === '1') ||
        documentRoot.classList.contains('auros-printbox-opening') ||
        documentRoot.classList.contains('auros-kit-relay') || this._editorTransitionUntil > now();
    }

    _blockers() {
      const reasons = [];
      if (!this._mounted || this._disposed || !this.isConnected) reasons.push('disconnected');
      if (!this.config || !this.config.enabled) reasons.push('disabled');
      if (!this._pageReady) reasons.push('page-loading');
      if (document.hidden || this._pageAway) reasons.push('hidden');
      if (this._manualPause) reasons.push('manual');
      if (this._reduced.matches) reasons.push('reduced-motion');
      if (this._mobile.matches && !this.config.mobile) reasons.push('mobile-disabled');
      if (!this._ids.size) reasons.push('no-characters');
      if (this._editorActive()) reasons.push('editor');
      return reasons;
    }

    _flockAllowed() {
      return Boolean(this.config?.flock && this.flockStage && this.querySelector('template[data-ah-ambient-bat]'));
    }

    _setState(value) {
      this._state = value;
      if (this.dataset.state !== value) this.dataset.state = value;
    }

    _clearTimer() {
      clearTimeout(this._timer);
      this._timer = 0;
      this._timerAt = 0;
    }

    _schedule(at) {
      if (this._timer && this._timerAt === at) return;
      this._clearTimer();
      this._timerAt = at;
      this._timer = setTimeout(() => {
        this._timer = 0;
        this._timerAt = 0;
        this._reconcile();
      }, Math.max(0, at - now()));
    }

    _reconcile() {
      if (!this._mounted || this._disposed) return;
      const reasons = this._blockers();
      if (reasons.length) {
        this._clearTimer();
        this._interruptScenes();
        this._nextAt = 0;
        this.hidden = true;
        this._setState(reasons.includes('page-loading') ? 'page-loading' : 'paused');
        if (reasons.length === 1 && reasons[0] === 'page-loading' && !this._error) this._prepare();
        return;
      }
      if (this._retryAt > now()) {
        this._setState('asset-error');
        this._schedule(this._retryAt);
        return;
      }
      if (!this._resourcesReady) {
        this._setState('loading');
        this._prepare();
        return;
      }
      if (this._active) { this._setState(this._scenes.size > 1 ? 'crossfading' : 'playing'); return; }
      this._clearTimer();
      this._begin();
    }

    _prepare() {
      if (this._loading || this._resourcesReady) return this._loading || Promise.resolve(true);
      const token = this._loadToken;
      const assets = this.config.assets;
      this._loading = (async () => {
        try {
          await Promise.all([
            loadResource(assets.css, 'css'),
            MODULES.vendor && MODULES.vendor.version === '3.15.0' ? Promise.resolve() : loadResource(assets.vendor, 'vendor'),
            MODULES.scenes && MODULES.scenes.version === ANIMATION_VERSION ? Promise.resolve() : loadResource(assets.scenes, 'scenes'),
            MODULES.effects && MODULES.effects.version === EFFECTS_VERSION ? Promise.resolve() : loadResource(assets.effects, 'effects'),
            !this._ids.has('spiders') ? Promise.resolve() : loadResource(assets.spidersCss, 'css'),
            !this._ids.has('spiders') || (MODULES.spiders && MODULES.spiders.version === SPIDERS_VERSION) ? Promise.resolve() : loadResource(assets.spiders, 'spiders')
          ]);
          if (token !== this._loadToken || !this._mounted || this._disposed) return false;
          if (!MODULES.vendor || !MODULES.vendor.gsap || !MODULES.vendor.MotionPathPlugin || !MODULES.vendor.MorphSVGPlugin ||
              !MODULES.scenes || typeof MODULES.scenes.create !== 'function' || typeof MODULES.scenes.createFlock !== 'function' || !MODULES.effects || typeof MODULES.effects.create !== 'function' || typeof MODULES.effects.measure !== 'function' || (this._ids.has('spiders') && (!MODULES.spiders || MODULES.spiders.version !== SPIDERS_VERSION || typeof MODULES.spiders.create !== 'function'))) {
            throw new Error('Halloween module contract is incomplete');
          }
          this._resourcesReady = true;
          this._error = '';
          this._retryAt = 0;
          return true;
        } catch (error) {
          if (token === this._loadToken && this._mounted && !this._disposed) {
            this._error = error instanceof Error ? error.message : 'Halloween assets failed';
            this._retryAt = now() + RETRY_DELAY;
          }
          return false;
        } finally {
          if (token === this._loadToken && this._mounted && !this._disposed) {
            this._loading = null;
            this._reconcile();
          }
        }
      })();
      return this._loading;
    }

    _fillQueue() {
      if (!this._queue.length) {
        this._queue = Array.from(this._ids).filter(id => id !== 'bats' && id !== 'spiders');
        for (let index = this._queue.length - 1; index > 0; index--) {
          const target = Math.floor(Math.random() * (index + 1));
          [this._queue[index], this._queue[target]] = [this._queue[target], this._queue[index]];
        }
        if (this._ids.has('spiders')) this._queue.unshift('spiders');
        if (this._ids.has('bats')) this._queue.unshift('bats');
        else if (this._queue.length > 1 && this._queue[0] === this._lastCharacter) [this._queue[0], this._queue[1]] = [this._queue[1], this._queue[0]];
        this._cycle++;
      }
    }

    _nextId(previewId) {
      if (previewId && this._ids.has(previewId)) return previewId;
      this._fillQueue();
      return this._queue.shift();
    }

    _measureFigure(template) {
      const id = template.dataset.ahCharacter;
      if (this._figureMetrics.has(id)) return this._figureMetrics.get(id);
      const svg = template.content.querySelector('svg').cloneNode(true);
      svg.style.cssText = 'position:fixed;left:0;top:0;width:240px;height:240px;visibility:hidden;pointer-events:none';
      document.body.appendChild(svg);
      let metrics;
      try {
        const view = svg.viewBox.baseVal;
        const figure = svg.querySelector('[data-ah-contrast="figure"]') || svg;
        const box = figure.getBBox();
        const units = view.width || 240;
        const centerX = view.x + units / 2;
        const centerY = view.y + (view.height || units) / 2;
        metrics = box.width > 0 ? {
          left: Math.max(0, (centerX - box.x) / units),
          right: Math.max(0, (box.x + box.width - centerX) / units),
          top: Math.max(0, (centerY - box.y) / units),
          bottom: Math.max(0, (box.y + box.height - centerY) / units)
        } : { left: .5, right: .5, top: .5, bottom: .5 };
      } finally { svg.remove(); }
      this._figureMetrics.set(id, metrics);
      return metrics;
    }

    _placement(template) {
      const width = documentRoot.clientWidth || innerWidth;
      const height = window.visualViewport ? Math.min(innerHeight, window.visualViewport.height) : innerHeight;
      const mobile = this._mobile.matches;
      const viewport = { width, height, mobile };
      const side = this._nextSide;
      const header = document.querySelector('.auros-site-header-stack') || document.querySelector('header');
      const headerBottom = header && visible(header) ? Math.min(height * 0.4, Math.max(0, header.getBoundingClientRect().bottom)) : 65;
      const bottomReserve = mobile ? 54 : 28;
      const edge = mobile ? 12 : 16;
      const freeHeight = Math.max(48, height - headerBottom - bottomReserve - edge * 2);
      const requestedSize = clamp(template.dataset.ahSize, 90, 220, 170) * this.config.scale * (mobile ? 1.12 : 1.70);
      const metrics = this._measureFigure(template);
      const mirrored = side === 'right' && template.dataset.ahKind === 'flight';
      const figureMetrics = { ...metrics, left: mirrored ? metrics.right : metrics.left, right: mirrored ? metrics.left : metrics.right };
      const envelope = MODULES.scenes.motionEnvelope || { rotation: 4, horizontalRadiusFactor: .4, verticalRadiusFactor: .5, articulationPadding: .12 };
      const layout = size => {
        const motionRadius = Math.min(mobile ? 36 : 50, size * (mobile ? .20 : .18));
        const motionRadiusX = Math.min(mobile ? 20 : 32, size * .12);
        const articulation = size * envelope.articulationPadding;
        const turn = size * .65 * Math.sin(envelope.rotation * Math.PI / 180);
        const padding = articulation + turn + 3;
        const figure = {
          left: size * (mirrored ? metrics.right : metrics.left) + padding,
          right: size * (mirrored ? metrics.left : metrics.right) + padding,
          top: size * metrics.top + padding,
          bottom: size * metrics.bottom + padding
        };
        const effects = MODULES.effects.measure({ id: template.dataset.ahCharacter, size, viewport, figureMetrics, config: { ...this.config, side } });
        const reachX = motionRadiusX * envelope.horizontalRadiusFactor;
        const reachY = motionRadius * envelope.verticalRadiusFactor;
        const scene = {
          left: Math.max(effects.left, figure.left + reachX),
          right: Math.max(effects.right, figure.right + reachX),
          top: Math.max(effects.top, figure.top + reachY),
          bottom: Math.max(effects.bottom, figure.bottom + reachY)
        };
        return { figure, scene, motionRadius, motionRadiusX };
      };
      let size = Math.max(32, Math.floor(Math.min(requestedSize, width * (mobile ? .46 : .36))));
      let measured = layout(size);
      // Use the same requested size on every template. Only the viewport and
      // complete animation bounds may limit it, never product text or images.
      for (let attempt = 0; attempt < 3; attempt++) {
        const maxWidth = mobile ? (width - edge * 3) / 2 : width - edge * 2;
        const fit = Math.min(1, maxWidth / (measured.scene.left + measured.scene.right), freeHeight / (measured.scene.top + measured.scene.bottom));
        if (fit >= .999) break;
        size = Math.max(24, Math.floor(size * fit));
        measured = layout(size);
      }
      const { figure, scene, motionRadius, motionRadiusX } = measured;
      const anchor = { x: side === 'left' ? edge + scene.left : width - edge - scene.right, y: headerBottom + edge + scene.top };
      const motionBounds = {
        left: edge + figure.left,
        right: Math.max(edge + figure.left, width - edge - figure.right),
        top: headerBottom + edge + figure.top,
        bottom: Math.max(headerBottom + edge + figure.top, height - bottomReserve - edge - figure.bottom)
      };
      return { viewport, size, side, zone: 'upper', anchor, motionRadius, motionRadiusX, motionBounds, sceneBounds: scene, figureMetrics };
    }

    _createScope(container, key) {
      const source = this.querySelector(':scope > .ah-definitions');
      if (!source) throw new Error('Halloween SVG materials are unavailable');
      const definitions = source.cloneNode(true);
      const materials = new Map();
      const ids = new Map();
      for (const node of definitions.querySelectorAll('[id]')) {
        const original = node.id;
        ids.set(original, key + '-' + original);
        materials.set(original, node);
        node.dataset.ahMaterial = original;
        node.id = ids.get(original);
      }
      const scopeSvg = root => {
        const nodes = root.nodeType === 1 ? [root, ...root.querySelectorAll('*')] : Array.from(root.querySelectorAll('*'));
        for (const node of nodes) {
          for (const attribute of Array.from(node.attributes)) {
            if (attribute.name === 'id') continue;
            const value = attribute.value.replace(/url\(\s*(['"]?)#([\w-]+)\1\s*\)/g, (match, quote, id) => {
              if (!ids.has(id)) return match;
              if (attribute.name === 'fill') node.dataset.ahFill = id;
              return 'url(#' + ids.get(id) + ')';
            });
            const reference = value.startsWith('#') && ids.has(value.slice(1)) ? '#' + ids.get(value.slice(1)) : value;
            if (reference !== attribute.value) node.setAttribute(attribute.name, reference);
          }
        }
        return root;
      };
      scopeSvg(definitions);
      container.appendChild(definitions);
      return { materials, scopeSvg };
    }

    _syncSceneState() {
      this.hidden = !this._scenes.size;
      if (this._active) this.dataset.scene = this._active.id;
      else delete this.dataset.scene;
      if (!this._active || this._active.id !== 'bats') this.style.removeProperty('--ah-flock-layer');
      if (this._scenes.size) this._setState(this._scenes.size > 1 ? 'crossfading' : 'playing');
    }

    _transition(record) {
      if (!this._scenes.has(record.key) || record !== this._active || record.handedOff) return;
      record.handedOff = true;
      record.phase = 'fading';
      record.container.dataset.ahPhase = 'fading';
      if (this._blockers().length) { this._reconcile(); return; }
      if (record.preview || (!this._queue.length && this._ids.has('bats'))) {
        this._nextAt = record.startedAt + DURATION;
        return;
      }
      this._fillQueue();
      if (this._queue[0] === 'bats' || this._queue[0] === 'spiders') {
        this._nextAt = record.startedAt + DURATION;
        return;
      }
      const elapsed = record.scene.timeline.time();
      this._begin('', true, Math.max(0, elapsed - TRANSITION_AT / 1000));
    }

    _begin(previewId = '', crossfade = false, offset = 0) {
      if (!this._mounted || this._disposed || !this._resourcesReady || this._blockers().length) return false;
      if (this._active && (!crossfade || this._active.id === 'bats' || this._active.id === 'spiders' || this._scenes.size >= 2)) return false;
      let id = '';
      let record = null;
      const requestedSide = this._nextSide;
      try {
        id = this._nextId(previewId);
        const isFlock = id === 'bats';
        const isSwarm = id === 'spiders';
        if ((isFlock || isSwarm) && this._scenes.size) throw new Error('Halloween special scenes cannot overlap a principal');
        const sceneDuration = isFlock ? FLOCK_DURATION : isSwarm ? SPIDERS_DURATION : DURATION;
        const gsap = MODULES.vendor.gsap;
        MODULES.sceneSequence = (MODULES.sceneSequence || 0) + 1;
        const key = 'ah-scene-' + MODULES.sceneSequence;
        const container = document.createElement('div');
        container.className = 'ah-scene';
        container.dataset.ahSceneKey = key;
        container.dataset.ahSceneId = id;
        container.dataset.ahPhase = isFlock ? 'flying' : isSwarm ? 'swarming' : 'entering';
        record = { key, id, container, side: requestedSide, preview: Boolean(previewId), scene: null, effects: null, actor: null, startedAt: 0, duration: sceneDuration / 1000, phase: isFlock ? 'flying' : isSwarm ? 'swarming' : 'entering', handedOff: false };
        const onComplete = () => this._completeScene(record);
        let placement;
        this.hidden = false;
        if (isFlock) {
          if (!this._flockAllowed()) throw new Error('Halloween flock is unavailable');
          const header = document.querySelector('.auros-site-header-stack') || document.querySelector('header');
          let flockLayer = 30;
          const headerNodes = header ? [header, ...header.querySelectorAll('header')] : [];
          const visited = new Set();
          for (const headerNode of headerNodes) {
            for (let node = headerNode; node && node.nodeType === 1 && !visited.has(node); node = node.parentElement) {
              visited.add(node);
              const layer = Number.parseInt(getComputedStyle(node).zIndex, 10);
              if (Number.isFinite(layer)) flockLayer = Math.max(flockLayer, layer + 1);
            }
          }
          this.style.setProperty('--ah-flock-layer', String(flockLayer));
          placement = { side: 'left', viewport: { width: documentRoot.clientWidth || innerWidth, height: window.visualViewport ? Math.min(innerHeight, window.visualViewport.height) : innerHeight, mobile: this._mobile.matches } };
          this.flockStage.appendChild(container);
          record.side = placement.side;
          record.scene = MODULES.scenes.createFlock({ gsap, stage: container, host: this, viewport: placement.viewport, config: { ...this.config, duration: sceneDuration / 1000 }, random, onComplete });
        } else if (isSwarm) {
          placement = { side: 'center', viewport: {
            width: documentRoot.clientWidth || innerWidth,
            height: window.visualViewport ? Math.min(innerHeight, window.visualViewport.height) : innerHeight,
            mobile: this._mobile.matches
          } };
          this.stage.appendChild(container);
          const scope = this._createScope(container, key);
          record.materials = scope.materials;
          record.side = 'center';
          record.scene = MODULES.spiders.create({ gsap, stage: container, host: this, viewport: placement.viewport, config: this.config, scopeSvg: scope.scopeSvg, random, onComplete });
        } else {
          const template = this._catalog.find(item => item.dataset.ahCharacter === id);
          if (!template) throw new Error('Halloween character is unavailable');
          placement = this._placement(template);
          this.stage.appendChild(container);
          const scope = this._createScope(container, key);
          record.materials = scope.materials;
          const actor = document.createElement('div');
          actor.className = 'ah-actor';
          actor.dataset.ahActor = id;
          actor.dataset.ahSide = placement.side;
          actor.dataset.ahSceneKey = key;
          actor.style.width = placement.size + 'px';
          const facing = document.createElement('div');
          facing.className = 'ah-actor__facing';
          if (placement.side === 'right' && template.dataset.ahKind === 'flight') facing.style.transform = 'scaleX(-1)';
          facing.appendChild(template.content.cloneNode(true));
          scope.scopeSvg(facing);
          actor.appendChild(facing);
          container.appendChild(actor);
          record.actor = actor;
          record.side = placement.side;
          const config = { ...this.config, ...placement, sceneIndex: this._shown };
          record.scene = MODULES.scenes.create({ gsap, actor, id, kind: template.dataset.ahKind || 'float', stage: container, viewport: placement.viewport, config, random, onComplete });
          record.effects = MODULES.effects.create({ gsap, stage: container, actor, id, viewport: placement.viewport, config, figureMetrics: placement.figureMetrics, materials: scope.materials, scopeSvg: scope.scopeSvg, random, timeline: record.scene.timeline });
          record.scene.timeline.call(() => {
            if (!this._scenes.has(key)) return;
            record.phase = 'holding';
            container.dataset.ahPhase = 'holding';
          }, [], FADE_DURATION / 1000);
          record.scene.timeline.call(() => this._transition(record), [], TRANSITION_AT / 1000);
        }
        if (!record.scene || !record.scene.timeline || typeof record.scene.kill !== 'function') throw new Error('Invalid scene: ' + id);
        const duration = record.scene.timeline.totalDuration();
        if (!Number.isFinite(duration) || Math.abs(duration - sceneDuration / 1000) > .01) throw new Error('Unexpected Halloween scene duration: ' + id);
        this._layoutWidth = placement.viewport.width;
        const startOffset = Math.min(FADE_DURATION / 1000, Math.max(0, offset));
        record.startedAt = now() - startOffset * 1000;
        this._scenes.set(key, record);
        this._active = record;
        this._nextAt = record.startedAt + (isFlock ? FLOCK_DURATION : isSwarm ? SPIDERS_DURATION : TRANSITION_AT);
        this._shown++;
        this._lastCharacter = id;
        if (!isSwarm) {
          this._lastSide = placement.side;
          this._nextSide = placement.side === 'left' ? 'right' : 'left';
        }
        this._error = '';
        this._retryAt = 0;
        this._syncSceneState();
        record.scene.timeline.play(startOffset);
        return true;
      } catch (error) {
        this._error = error instanceof Error ? error.message : 'Halloween initialization failed';
        this._retryAt = now() + RETRY_DELAY;
        if (record) this._removeScene(record);
        this._finishScene(true);
        if (id && !previewId) this._queue.unshift(id);
        this._nextSide = requestedSide;
        this._setState('asset-error');
        this._schedule(this._retryAt);
        return false;
      }
    }

    _removeScene(record) {
      if (!record || record.removed) return;
      record.removed = true;
      this._scenes.delete(record.key);
      try { if (record.effects && typeof record.effects.kill === 'function') record.effects.kill(); } catch (_) {}
      try { if (record.scene) record.scene.kill(); } catch (_) {}
      record.container.remove();
    }

    _completeScene(record) {
      if (!this._scenes.has(record.key)) return;
      this._removeScene(record);
      if (this._active === record) this._active = Array.from(this._scenes.values()).at(-1) || null;
      this._syncSceneState();
      if (!this._active && this._mounted && !this._disposed) {
        this._nextAt = 0;
        this._reconcile();
      }
    }

    _interruptScenes() {
      const latest = this._active;
      if (latest && !latest.preview) {
        this._queue.unshift(latest.id);
        if (latest.id !== 'bats' && latest.id !== 'spiders') this._nextSide = latest.side;
      }
      this._finishScene(true);
    }

    _finishScene(interrupted) {
      this._active = null;
      for (const record of this._scenes.values()) this._removeScene(record);
      if (this.stage) this.stage.replaceChildren();
      if (this.flockStage) this.flockStage.replaceChildren();
      this.hidden = true;
      this._nextAt = 0;
      this.style.removeProperty('--ah-flock-layer');
      delete this.dataset.scene;
      if (!interrupted && this._mounted && !this._disposed) this._reconcile();
    }


    _unmount() {
      if (!this._mounted) return;
      this._disposed = true;
      this._loadToken++;
      this._clearTimer();
      clearTimeout(this._editorTimer);
      this._finishScene(true);
      this._abort.abort();
      this._observer.disconnect();
      this._editorObserver.disconnect();
      this._editorNodes.clear();
      this._editorAncestors.clear();
      this._editorAttributeState.clear();
      this._figureMetrics.clear();
      this._mounted = false;
      this._loading = null;
      if (MODULES.instance === this) MODULES.instance = null;
    }

    pause() { this._manualPause = true; this._reconcile(); }
    resume() { this._manualPause = false; this._reconcile(); }
    stop() { this.pause(); }
    async preview(id = '') {
      if (id === 'spider') id = 'spiders';
      if (!this.config || !this.config.designMode || (id && !this._ids.has(id))) return false;
      if (!this._resourcesReady) await this._prepare();
      if (!this._resourcesReady || this._blockers().length) return false;
      this._clearTimer();
      this._finishScene(true);
      this._nextAt = 0;
      return this._begin(id);
    }
    diagnostics() {
      const flock = this._active?.id === 'bats' ? this._active.scene : null;
      const flockDiagnostics = typeof flock?.diagnostics === 'function' ? flock.diagnostics() : flock?.diagnostics || {};
      return {
        version: VERSION,
        state: this._state || this.dataset.state || 'inactive',
        enabled: Boolean(this.config && this.config.enabled),
        scope: 'all',
        initialDelaySeconds: 0,
        intervalSeconds: TRANSITION_AT / 1000,
        durationSeconds: DURATION / 1000,
        holdDurationSeconds: HOLD_DURATION / 1000,
        fadeDurationSeconds: FADE_DURATION / 1000,
        nextTransitionAt: this._nextAt || 0,
        activeScenes: Array.from(this._scenes?.values() || []).map(record => ({ key: record.key, id: record.id, side: record.side, startedAt: record.startedAt, duration: record.duration, phase: record.id === 'bats' ? 'flying' : record.id === 'spiders' ? 'swarming' : record.scene.timeline.time() < FADE_DURATION / 1000 ? 'entering' : record.scene.timeline.time() < TRANSITION_AT / 1000 ? 'holding' : 'fading', elapsed: record.scene.timeline.time() })),
        flockDurationSeconds: FLOCK_DURATION / 1000,
        spidersDurationSeconds: SPIDERS_DURATION / 1000,
        unlimited: true,
        sessionBased: false,
        pageLoaded: Boolean(this._pageReady),
        actorSize: Number.parseFloat(this._active?.actor?.style.width) || 0,
        characterCount: this._catalog ? this._catalog.length : 0,
        characters: this._catalog ? this._catalog.map(template => template.dataset.ahCharacter) : [],
        sceneCount: this._ids ? this._ids.size : 0,
        cycle: this._cycle || 0,
        remainingInCycle: this._queue ? this._queue.slice() : [],
        shown: this._shown || 0,
        remainingSeconds: this._nextAt ? Math.ceil(Math.max(0, this._nextAt - now()) / 1000) : 0,
        lastCharacter: this._lastCharacter || '',
        lastSide: this._lastSide || '',
        nextSide: this._nextSide || 'left',
        flock: { enabled: Boolean(this.config?.flock), active: Boolean(flock), ...flockDiagnostics },
        pausedReasons: this._mounted ? this._blockers() : ['inactive'],
        error: this._error || ''
      };
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, AurosHalloween);
  window.AurosHalloween = Object.freeze({
    version: VERSION,
    pause: () => MODULES.instance && MODULES.instance.pause(),
    resume: () => MODULES.instance && MODULES.instance.resume(),
    stop: () => MODULES.instance && MODULES.instance.stop(),
    preview: id => MODULES.instance ? MODULES.instance.preview(id) : Promise.resolve(false),
    diagnostics: () => MODULES.instance ? MODULES.instance.diagnostics() : { version: VERSION, state: 'inactive', enabled: false, characterCount: 0 }
  });
})();
